import React, { useState, useEffect, useCallback } from 'react';
import { saveToRecentCache, getRecentFromCache } from '../utils/cache';
import LoadingScreen from './LoadingScreen';
import Modal from './Modal';
import SearchModal from './SearchModal';
import Header from './Header';
import Banner from './Banner';
import ContentRow from './ContentRow';
import DonateModal from './DonateModal';
import { useMovieData } from '../hooks/useMovieData';

// --- Constants for Readability (Solution #4) ---
const IMG_URL = 'https://image.tmdb.org/t/p/original';
const BASE_URL = 'https://api.themoviedb.org/3';
const API_KEY = process.env.REACT_APP_TMDB_API_KEY;

const ANIMATION_DELAY_SHORT = 100;
const MODAL_TRANSITION_IN_DURATION = 150;
const MODAL_TRANSITION_OUT_DURATION = 250;
const LOADING_SCREEN_EXIT_DELAY = 1500;

export default function Main() {
  const {
    isLoading,
    setIsLoading,
    loadingProgress,
    bannerItem,
    movies,
    tvShows,
    anime,
    koreanDramas,
    recentlyWatched,
    setRecentlyWatched
  } = useMovieData();

  const [selectedItem, setSelectedItem] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchModalTransitioning, setSearchModalTransitioning] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchState, setSearchState] = useState({
    query: '',
    results: []
  });
  const [modalOpenedFromSearch, setModalOpenedFromSearch] = useState(false);
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);

  // --- Functions Memoized with useCallback (Solution #2) ---

  const openDetails = useCallback(async (item, fromSearch = false, searchData = null) => {
    try {
      const url = item.media_type === 'movie' 
        ? `${BASE_URL}/movie/${item.id}?api_key=${API_KEY}`
        : `${BASE_URL}/tv/${item.id}?api_key=${API_KEY}`;
      
      const res = await fetch(url);
      const details = await res.json();
      
      const fullItem = {
        ...item,
        ...details,
        media_type: item.media_type || (details.first_air_date ? 'tv' : 'movie'),
        currentSeason: item.currentSeason || 1,
        currentEpisode: item.currentEpisode || 1
      };

      setSelectedItem(fullItem);
      setModalOpenedFromSearch(fromSearch);

      if (fromSearch && searchData) {
        setSearchState({
          query: searchData.query,
          results: searchData.results
        });
      }
    } catch (error) {
      console.error('Error fetching details:', error);
      const fallbackItem = {
        ...item,
        media_type: item.media_type || 'movie',
        currentSeason: item.currentSeason || 1,
        currentEpisode: item.currentEpisode || 1
      };
      setSelectedItem(fallbackItem);
      setModalOpenedFromSearch(fromSearch);
    }
  }, []); // Setter functions from useState are stable and don't need to be dependencies

  const closeDetails = useCallback(() => {
    const wasOpenedFromSearch = modalOpenedFromSearch;
    const currentSearchState = searchState;
    
    setSelectedItem(null);
    setModalOpenedFromSearch(false);
    
    if (wasOpenedFromSearch && (currentSearchState.query || currentSearchState.results.length > 0)) {
      setTimeout(() => {
        setSearchModalTransitioning(true);
        setTimeout(() => {
          setShowSearchModal(true);
          setSearchModalTransitioning(false);
        }, MODAL_TRANSITION_IN_DURATION);
      }, ANIMATION_DELAY_SHORT);
    }
  }, [modalOpenedFromSearch, searchState]);

  const toggleSearchModal = useCallback(() => {
    if (showSearchModal) {
      setSearchModalTransitioning(true);
      setTimeout(() => {
        setShowSearchModal(false);
        setSearchModalTransitioning(false);
      }, MODAL_TRANSITION_OUT_DURATION);
    } else {
      setSearchModalTransitioning(true);
      setTimeout(() => {
        setShowSearchModal(true);
        setSearchModalTransitioning(false);
      }, MODAL_TRANSITION_IN_DURATION);
      
      if (!modalOpenedFromSearch) {
        setSearchState({ query: '', results: [] });
      }
    }
  }, [showSearchModal, modalOpenedFromSearch]);

  const addToRecentlyWatched = useCallback((item, season = null, episode = null) => {
    saveToRecentCache(item, season, episode);
    setRecentlyWatched(getRecentFromCache());
  }, [setRecentlyWatched]);

  const handleRecommendationSelect = useCallback((recommendedItem) => {
    setSelectedItem(null);
    setTimeout(() => {
      openDetails(recommendedItem, false, null);
    }, ANIMATION_DELAY_SHORT);
  }, [openDetails]); // Depends on the memoized openDetails function

  // Handle loading screen with delay
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowLoadingScreen(false);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, ANIMATION_DELAY_SHORT);
      }, LOADING_SCREEN_EXIT_DELAY);

      return () => clearTimeout(timer);
    } else {
      setShowLoadingScreen(true);
    }
  }, [isLoading]);

  // Handle header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {showLoadingScreen && <LoadingScreen progress={loadingProgress} />}
      
      <Header 
        scrolled={scrolled} 
        isLoading={showLoadingScreen}
        toggleSearchModal={toggleSearchModal}
        setIsDonateModalOpen={setIsDonateModalOpen}
      />

      <Banner 
        bannerItem={bannerItem} 
        IMG_URL={IMG_URL} 
        onPlayClick={() => openDetails(bannerItem)}
      />

      <main className="relative z-10 mt-4 md:mt-8 pb-16">
        {recentlyWatched.length > 0 && (
          <ContentRow
            title="Continue Watching"
            items={recentlyWatched}
            IMG_URL={IMG_URL}
            onItemClick={openDetails}
            showRecentIndicator={true}
          />
        )}
        
        <ContentRow
          title="Top 10 TV Shows in Philippines"
          items={tvShows}
          IMG_URL={IMG_URL}
          onItemClick={openDetails}
          showRank={true}
        />
        {koreanDramas.length > 0 && (
          <ContentRow 
            title="Latest Korean Drama"
            items={koreanDramas}
            IMG_URL={IMG_URL}
            onItemClick={openDetails}
            showDateIndicator={true}
            isKoreanDramas={true}
          />
        )}
        <ContentRow
          title="Trending Now"
          items={movies}
          IMG_URL={IMG_URL}
          onItemClick={openDetails}
        />  

        <ContentRow
          title="Anime"
          items={anime}
          IMG_URL={IMG_URL}
          onItemClick={openDetails}
        />
      </main>

      {selectedItem && (
        <Modal 
          item={selectedItem} 
          onClose={closeDetails}
          onAddToRecent={addToRecentlyWatched}
          onItemSelect={handleRecommendationSelect}
        />
      )}

      {(showSearchModal || searchModalTransitioning) && (
        <SearchModal 
          onClose={toggleSearchModal} 
          onItemSelect={(item, searchData) => {
            openDetails(item, true, searchData);
            setSearchModalTransitioning(true);
            setTimeout(() => {
              setShowSearchModal(false);
              setSearchModalTransitioning(false);
            }, MODAL_TRANSITION_OUT_DURATION);
          }}
          lastSearch={searchState}
          isVisible={showSearchModal}
          isTransitioning={searchModalTransitioning}
        />
      )}

      <DonateModal 
        isOpen={isDonateModalOpen} 
        onClose={() => setIsDonateModalOpen(false)} 
      />
    </div>
  );
}